import { supabase } from './supabase';

export interface PostmarkOutboundMessage {
  messageId: string;
  messageStream: string | null;
  receivedAt: string;
  from: string | null;
  subject: string | null;
  status: string | null;
  tag: string | null;
  trackOpens: boolean;
  trackLinks: string | null;
  trackingEnabled: boolean;
  opened: boolean;
  openedAt: string | null;
  clickCount: number;
  lastClickAt: string | null;
  lastClickUrl: string | null;
}

export interface PostmarkMessageEvent {
  type: string;
  receivedAt: string;
  recipient: string | null;
  summary: string | null;
  link: string | null;
  bounceType: string | null;
  bounceDescription: string | null;
}

export interface PostmarkOutboundMessageDetail extends PostmarkOutboundMessage {
  to: string[];
  events: PostmarkMessageEvent[];
}

export interface PostmarkOutboundMessagesResult {
  totalCount: number;
  messages: PostmarkOutboundMessage[];
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('postmark-outbound-messages', {
    body,
  });
  if (error) {
    let detail: string | null = null;
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const parsed = await (ctx as Response).clone().json();
        if (parsed && typeof parsed.error === 'string') detail = parsed.error;
      } catch {
        try {
          detail = await (ctx as Response).clone().text();
        } catch {
          /* ignore */
        }
      }
    }
    if (!detail) {
      detail = (data as { error?: string } | null)?.error ?? null;
    }
    throw new Error(detail || error.message);
  }
  return data as T;
}

export async function loadPostmarkOutboundMessages(
  email: string,
  opts?: { count?: number; offset?: number; tag?: string },
): Promise<PostmarkOutboundMessagesResult> {
  const body: Record<string, unknown> = {
    email: email.trim(),
    count: opts?.count,
    offset: opts?.offset,
  };
  if (opts?.tag?.trim()) body.tag = opts.tag.trim();
  const data = await invoke<PostmarkOutboundMessagesResult>(body);
  return {
    totalCount: data.totalCount ?? 0,
    messages: data.messages ?? [],
  };
}

export async function loadPostmarkOutboundMessageDetails(
  messageId: string,
  email?: string | null,
): Promise<PostmarkOutboundMessageDetail> {
  const body: Record<string, unknown> = { messageId: messageId.trim() };
  if (email?.trim()) body.email = email.trim();
  const data = await invoke<{ message: PostmarkOutboundMessageDetail }>(body);
  if (!data?.message) {
    throw new Error('Postmark returned no message detail');
  }
  return {
    ...data.message,
    to: Array.isArray(data.message.to) ? data.message.to : [],
    events: Array.isArray(data.message.events) ? data.message.events : [],
  };
}
